import React, { useState } from 'react';
import './settlements.css';
import { getSupabase } from '../lib/supabase.js';
import { loadPoolEntry, editPoolEntry, deletePoolEntry, goldAmountEditable, poolTypeLabel } from '../lib/poolEntries.js';

// Admin Edit / Delete for one pool entry (gold payout, investment payout,
// ledger carryover, and — on Balances — pool payments). Shared by the Home
// feed drawer, the Activity ledger and Balances → Pool activity, so all three
// behave the same. `entry` needs { movementId, poolType, status? }; a pending
// entry gets Reject instead of Delete. onDone runs after a successful change.

const fmt$ = (n) => {
  const v = Math.abs(Number(n || 0));
  return (Number(n) < 0 ? '-' : '') + '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const NO_WHEEL = { onWheel: (e) => e.currentTarget.blur() };

export function PoolEntryActions({ entry, onDone, compact = false }) {
  const [mode, setMode] = useState(null); // null | 'edit' | 'delete'
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const open = async (m, evt) => {
    if (evt) evt.stopPropagation();
    setErr(null);
    setBusy(true);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Supabase is unavailable.');
      setDetail(await loadPoolEntry(sb, entry.movementId));
      setMode(m);
    } catch (e) {
      alert((e && e.message) || String(e));
    } finally { setBusy(false); }
  };
  const close = () => { setMode(null); setDetail(null); setErr(null); };
  const run = async (fn) => {
    setBusy(true); setErr(null);
    try {
      await fn(getSupabase());
      close();
      if (typeof onDone === 'function') onDone();
    } catch (e) {
      setErr((e && e.message) || String(e));
    } finally { setBusy(false); }
  };

  const btnStyle = compact ? { padding: '3px 9px', fontSize: 11.5 } : { padding: '6px 12px', fontSize: 12 };
  return (
    <>
      <span className="pool-entry-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="btn-ghost" disabled={busy} style={btnStyle} onClick={(e) => open('edit', e)}>Edit</button>
        <button type="button" className="btn-ghost ad-delete-btn" disabled={busy} style={{ ...btnStyle, color: 'var(--rose, #e11d48)' }} onClick={(e) => open('delete', e)}>{(entry.status || entry.movementStatus) === 'pending' ? 'Reject' : 'Delete'}</button>
      </span>
      {mode === 'edit' && detail && (
        <EditPoolEntryModal entry={entry} detail={detail} busy={busy} err={err} onCancel={close}
          onSave={(patch) => run((sb) => editPoolEntry(sb, detail, patch))} />
      )}
      {mode === 'delete' && detail && (
        <DeletePoolEntryConfirm entry={entry} detail={detail} busy={busy} err={err} onCancel={close}
          onConfirm={(opts) => run((sb) => deletePoolEntry(sb, detail, opts))} />
      )}
    </>
  );
}

function linkedLine(detail) {
  const l = detail.linked;
  if (!l) return null;
  if (l.table === 'gold_payments') {
    const d = l.row.data || {};
    return 'gold payment of ' + (d.paymentDate || '?') + ' on Gold → Settle';
  }
  return 'payout row of ' + (l.row.settle_date || '?') + ' on Settlements';
}

function EditPoolEntryModal({ entry, detail, busy, err, onCancel, onSave }) {
  const { mv, linked } = detail;
  const [amount, setAmount] = useState(String(mv.amount));
  const [date, setDate] = useState(String(mv.movement_date || '').slice(0, 10));
  const [notes, setNotes] = useState(mv.notes || '');
  const amountLocked = linked?.table === 'gold_payments' && !goldAmountEditable(linked.row.data);
  const link = linkedLine(detail);
  return (
    <div className="settle-modal-backdrop" onClick={onCancel} role="presentation">
      <form className="settle-modal" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); onSave({ amount, date, notes }); }}>
        <div className="settle-modal-head">
          <div>
            <div className="settle-modal-eyebrow">TK-C pool</div>
            <h3 className="settle-modal-title">Edit {poolTypeLabel(entry.poolType).toLowerCase()}</h3>
          </div>
          <button type="button" aria-label="Close" className="settle-modal-close" onClick={onCancel}>✕</button>
        </div>
        <label className="settle-modal-field">
          <span>Amount ($)</span>
          <input {...NO_WHEEL} type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={busy || amountLocked} required autoFocus />
        </label>
        {amountLocked && (
          <div className="pool-entry-note">This gold payment is split (cash plus expenses, or BGS plus profit), so its amount changes on Gold → Settle. Date and notes can change here.</div>
        )}
        <label className="settle-modal-field">
          <span>Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={busy} required />
        </label>
        <label className="settle-modal-field">
          <span>Notes</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} />
        </label>
        {link && <div className="pool-entry-note">The {link} gets the same amount and date.</div>}
        {err && <div className="pool-entry-err" role="alert">{err}</div>}
        <div className="settle-modal-actions">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </form>
    </div>
  );
}

function DeletePoolEntryConfirm({ entry, detail, busy, err, onCancel, onConfirm }) {
  const { mv, linked } = detail;
  const pending = mv.status === 'pending';
  const [alsoLinked, setAlsoLinked] = useState(true);
  const link = linkedLine(detail);
  return (
    <div className="settle-modal-backdrop" onClick={onCancel} role="presentation">
      <div className="settle-modal settle-modal-danger" onClick={(e) => e.stopPropagation()} role="alertdialog">
        <div className="settle-modal-head">
          <div>
            <div className="settle-modal-eyebrow" style={{ color: 'var(--rose, #e11d48)' }}>TK-C pool</div>
            <h3 className="settle-modal-title">{pending ? 'Reject' : 'Delete'} this {poolTypeLabel(entry.poolType).toLowerCase()}?</h3>
          </div>
          <button type="button" aria-label="Close" className="settle-modal-close" onClick={onCancel}>✕</button>
        </div>
        <p style={{ margin: '0 0 12px', color: 'var(--text-2, #475569)', fontSize: 13.5, lineHeight: 1.5 }}>
          {pending
            ? <><strong className="mono">{fmt$(mv.amount)}</strong> on {String(mv.movement_date).slice(0, 10)} is still waiting for your confirmation. Rejecting it means it never counts toward the pool — use this for a duplicate or a transfer that never arrived.</>
            : <><strong className="mono">{fmt$(mv.amount)}</strong> on {String(mv.movement_date).slice(0, 10)} comes out of the pool balance. It's kept on record as reversed, and it can't be undone.</>}
        </p>
        {link && (
          <label className="pool-entry-check">
            <input type="checkbox" checked={alsoLinked} onChange={(e) => setAlsoLinked(e.target.checked)} disabled={busy} />
            <span>
              {linked.table === 'gold_payments'
                ? <>Also delete its {link} (same as Del there)</>
                : <>Also remove its {link}</>}
            </span>
          </label>
        )}
        {err && <div className="pool-entry-err" role="alert">{err}</div>}
        <div className="settle-modal-actions">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary settle-modal-danger-btn" disabled={busy}
            onClick={() => onConfirm({ alsoLinked })}>{busy ? (pending ? 'Rejecting…' : 'Deleting…') : (pending ? 'Yes, reject it' : 'Yes, delete it')}</button>
        </div>
      </div>
    </div>
  );
}
